import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Label,
  Form,
  Button,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Modal,
  Input,
  FormGroup,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { post, get } from "../../helpers/api_helper";
import {
  LEDGER_REPORT,
  SEND_PDF,
  CUSTOMER_LIST,
} from "../../helpers/url_helper";
import { useForm, Controller, set } from "react-hook-form";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import toastr from "toastr";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import FullReportPdf from "./FullReportPdf";
import Select from "react-select";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const FullReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [customerName, setCustomerName] = useState();

  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Full Reports", link: "#" },
  ];

  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      customer_ul_id: [],
      start_date: new Date().toISOString().split("T")[0],
      end_date: new Date().toISOString().split("T")[0],
      report_type: "full_ledger",
    },
  });

  // Fetch customers list with pagination
  const fetchCustomers = async () => {
    try {
      setIsFetchingCustomers(true);

      // Make API call with large page size to get all customers
      const response = await get(`${CUSTOMER_LIST}?page=0&size=1000`); // Adjust size as needed

      if (!response || !response.data) {
        throw new Error("Invalid API response structure");
      }

      // Handle both possible response structures
      const customersData = response.data.items || response.data;

      if (!Array.isArray(customersData)) {
        throw new Error("Expected array of customers");
      }

      const newCustomers = customersData.map((customer) => ({
        value: customer.ul_id,
        label: `${customer.phone} : ${customer.name}`,
        ...customer,
      }));

      setCustomers(newCustomers);
      toastr.success(`Loaded ${newCustomers.length} customers`);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      toastr.error("Failed to load customer list");
      setCustomers([]);
    } finally {
      setIsFetchingCustomers(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchReport = async (formData) => {
    try {
      setLoading(true);
      const startDate = new Date(formData.start_date);
      const syyyy = startDate.getFullYear();
      const smm = String(startDate.getMonth() + 1).padStart(2, "0");
      const sdd = String(startDate.getDate()).padStart(2, "0");

      const endDate = new Date(formData.end_date);
      const eyyyy = endDate.getFullYear();
      const emm = String(endDate.getMonth() + 1).padStart(2, "0");
      const edd = String(endDate.getDate()).padStart(2, "0");
      setFromDate(`${syyyy}-${smm}-${sdd}`);
      setToDate(`${eyyyy}-${emm}-${edd}`);

      const payload = {
        customer_ul_id: formData.customer_ul_id.value,
        start_date: `${syyyy}-${smm}-${sdd}`,
        end_date: `${eyyyy}-${emm}-${edd}`,
        report_type: "full_ledger",
      };

      const response = await post(LEDGER_REPORT, payload);

      setReportData({
        ...response.data,
        // Include the selected customer objects for the PDF
        selectedCustomers: formData.customer_ul_id.value,
        fromDate: `${syyyy}-${smm}-${sdd}`,
        toDate: `${eyyyy}-${emm}-${edd}`,
      });
      setCustomerName(response.data.customer.name);
      setShowPdfModal(true);
    } catch (error) {
      console.error("Failed to fetch report:", error);
      toastr.error("Failed to generate report.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = useCallback(async () => {
    if (!reportData) return;
    console.log(reportData);
    reportData["email"] = reportData.customer?.email;
    try {
      const emailPayload = {
        pdf_data: reportData,
        email: reportData.customer?.email,
        customer_name: reportData.customer?.name,
        report_type: "full_ledger",
        start_date: fromDate,
        end_date: toDate,
      };

      await post(SEND_PDF, emailPayload);
      toastr.success("Email sent successfully");
    } catch (error) {
      console.error("Failed to send email:", error);
      toastr.error("Failed to send email");
    }
  }, [reportData]);

const handleExportExcel = () => {
  const entries = reportData.allEntries || [];
  if (!Array.isArray(entries) || entries.length === 0) {
    toastr.warning("No data to export.");
    return;
  }
  const exportData = entries.map((entry) => ({
    Date: entry.date || "",
    Description: entry.description || "",
    Vehicle: entry.vehicle_no || "",
    Quantity: entry.quantity || "",
    Rate: entry.rate || "",
    Debit: entry.debit || "",
    Credit: entry.credit || "",
    Discount: entry.discount_amount || "",
    Balance: entry.balance || "",
    Type: entry.type || "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger Report");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

  saveAs(blob, `${reportData.customer.name}-ledger-${reportData.fromDate}-to-${reportData.toDate}.xlsx`);
};



  const selectedCustomers = watch("customer_ul_id");

  return (
    <React.Fragment>
      <div className="page-content ">
        <Container fluid style={{minHeight: "70vh"}}>
          <Breadcrumbs title="Full Report" breadcrumbItems={breadcrumbItems} />

          <Card >
            <CardBody>
              <Form onSubmit={handleSubmit(fetchReport)}>
                <Row className="align-items-end">
                  <Col md={isMultiSelect ? 4 : 3}>
                      <Label>Select Customer{isMultiSelect ? "s" : ""}</Label>
                      <Controller
                        name="customer_ul_id"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={customers} // Now filters existing data
                            isMulti={isMultiSelect}
                            isLoading={isFetchingCustomers}
                            placeholder={
                              isFetchingCustomers
                                ? "Loading customers..."
                                : "Select customer..."
                            }
                            isClearable
                            isSearchable
                            classNamePrefix="select2-selection"
                            noOptionsMessage={() =>
                              "No matching customers found"
                            }
                            getOptionLabel={(option) =>
                              `${option.phone} : ${option.name}`
                            }
                            getOptionValue={(option) => option.ul_id}
                          />
                        )}
                      />
                    <div className="form-check form-switch mt-2">
                      <Input
                        type="switch"
                        id="multiSelectSwitch"
                        checked={isMultiSelect}
                        onChange={() => {
                          setIsMultiSelect(!isMultiSelect);
                          setValue("customer_ul_id", []); // Reset selection when changing mode
                        }}
                      />
                      <Label for="multiSelectSwitch" check>
                        Enable multi-select
                      </Label>
                    </div>
                  </Col>

                  <Col md={2}>
                    <Label>Start Date</Label>
                    <Controller
                      name="start_date"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Flatpickr
                          {...field}
                          className="form-control"
                          options={{
                            dateFormat: "Y-m-d",
                            maxDate: "today",
                          }}
                        />
                      )}
                    />
                  </Col>
                  <Col md={2}>
                    <Label>End Date</Label>
                    <Controller
                      name="end_date"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Flatpickr
                          {...field}
                          className="form-control"
                          options={{
                            dateFormat: "Y-m-d",
                            maxDate: "today",
                          }}
                        />
                      )}
                    />
                  </Col>
                  <Col md={2}>
                    <Button
                      color="primary"
                      type="submit"
                      className="w-100"
                      disabled={
                        loading ||
                        !selectedCustomers ||
                        (Array.isArray(selectedCustomers) &&
                          selectedCustomers.length === 0)
                      }
                    >
                      {loading ? "Generating..." : "Generate Report"}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal
        isOpen={showPdfModal}
        toggle={() => setShowPdfModal(false)}
        size="xl"
      >
        <ModalHeader toggle={() => setShowPdfModal(false)}>
          {"Report Preview"}
        </ModalHeader>
        <ModalBody style={{ height: "80vh" }}>
          {reportData && (
            <PDFViewer width="100%" height="100%">
              <FullReportPdf data={reportData} />
            </PDFViewer>
          )}
        </ModalBody>
        <ModalFooter>
          {reportData && (
            <>
              <PDFDownloadLink
                document={<FullReportPdf data={reportData} />}
                fileName={`${customerName}-ledger-${fromDate}-to-${toDate}.pdf`}
                className="btn btn-success"
              >
                {({ loading }) => (loading ? "Preparing..." : "Download PDF")}
              </PDFDownloadLink>
               <Button color="primary" onClick={handleExportExcel} className="bg-purple">
                <i className="mdi mdi-file-excel me-1 " /> Export Excel
              </Button>
              <Button
                color="primary"
                onClick={handleSendEmail}
                disabled={!reportData.customer?.email}>
                <i className="mdi mdi-email me-1"></i> Send Pdf Email
              </Button>
            </>
          )}
          <Button color="secondary" onClick={() => setShowPdfModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default FullReport;
